import React, { useState } from 'react';
import {
  Grid,
  Toolbar,
  Stack,
  Button,
  TextField,
  Box,
  FormControl,
  CircularProgress,
} from '@mui/material';

import { useUser, useUserDispatch } from '@/providers';
import { SET_USER } from '@/actions/types';
import { updateUser, deleteUser } from '@/actions/user';

import DisplayNameField from './DisplayNameField';
import GivenNameField from './GivenNameField';
import LastNameField from './LastNameField';
import CouponField from './CouponField';

export default function ProfileForm({ edit, onSubmit }) {
  const user = useUser();
  const userDispatch = useUserDispatch();

  const [pristine, setPristine] = useState(Boolean(edit));
  const [loading, setLoading] = useState(false);
  const [given_name, setGivenName] = useState(user?.given_name);
  const [given_name_error, setGivenNameError] = useState(false);
  const [family_name, setFamilyName] = useState(user?.family_name);
  const [family_name_error, setFamilyNameError] = useState(false);
  const [display_name, setDisplayName] = useState(user?.display_name);
  const [display_name_error, setDisplayNameError] = useState(false);
  const [coupon_code, setCouponCode] = useState('');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updatedUser = await updateUser({
        given_name,
        family_name,
        display_name,
        coupon_code: Boolean(coupon_code) ? coupon_code : undefined,
      });
      userDispatch({ type: SET_USER, payload: updatedUser });
      setLoading(false);
      setPristine(true);
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (edit) {
      setGivenName(user?.given_name);
      setFamilyName(user?.family_name);
      setDisplayName(user?.display_name);
      setPristine(true);
    } else {
      await deleteUser();
      userDispatch({ type: SET_USER, payload: null });
    }
  };

  if (!user) {
    return <div>User Not Defined</div>;
  }

  return (
    <Grid container spacing={2} sx={{ maxWidth: 600 }}>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <Box>
          <FormControl fullWidth required>
            <TextField
              label='Email'
              id='email'
              variant='outlined'
              value={user.email}
              disabled
            />
          </FormControl>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <DisplayNameField
          value={display_name}
          error={display_name_error}
          setValue={setDisplayName}
          setError={setDisplayNameError}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <GivenNameField
          value={given_name}
          error={given_name_error}
          setValue={setGivenName}
          setError={setGivenNameError}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <LastNameField
          value={family_name}
          error={family_name_error}
          setValue={setFamilyName}
          setError={setFamilyNameError}
          setChanged={() => setPristine(false)}
        />
      </Grid>
      {!edit ? (
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <CouponField
            value={coupon_code}
            setValue={setCouponCode}
            setChanged={() => setPristine(false)}
          />
        </Grid>
      ) : undefined}
      <Grid size={{ xs: 12, md: 12, lg: 12 }}>
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <Button
              variant='contained'
              color='brand'
              onClick={handleSubmit}
              disabled={
                pristine ||
                Boolean(given_name_error) ||
                Boolean(display_name_error) ||
                Boolean(family_name_error) ||
                !Boolean(given_name) ||
                !Boolean(display_name) ||
                !Boolean(family_name) ||
                loading
              }
              startIcon={loading ? <CircularProgress size={20} /> : undefined}
            >
              {edit ? 'Update' : 'Next'}
            </Button>

            <Button
              variant='outlined'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>
    </Grid>
  );
}
